import React from "react";
import "./styles/LicenseAgreement.css";
import { APP_NAME } from "../common/constants";
import SEO from "./SEO";

const LicenseAgreement: React.FC = () => {
  return (
    <>
      <SEO
        title={`${APP_NAME} License Agreement & Terms of Use`}
        description={`Review the legal license terms and acceptable use policy for using ${APP_NAME}. Learn what you're allowed to do, your responsibilities, and important limitations and restrictions.`}
      />
      <div className="license-container">
        <h1>{APP_NAME} License Agreement & Terms of Use</h1>

        <p>
          <strong>TL;DR;</strong>
          <br />
          <strong>Free Plan:</strong> Try {APP_NAME} with limited features and usage. For personal, low-volume use with content you own or are licensed to use.
          <br />
          <strong>Basic Plan:</strong> For individuals who want more generous limits and features for personal or commercial projects.
          <br />
          <strong>Pro Plan:</strong> For professionals, freelancers, and small teams working on multiple or client projects.
          <br />
          <strong>Developer Plan:</strong> For advanced and higher-volume use, including integrations and developer workflows (e.g. API usage, automation) as described on our pricing page.
          <br />
          <strong>Important:</strong> You are always responsible for ensuring you have the legal right to upload, process, cut, clip, convert, or otherwise handle any content through {APP_NAME}. Do not use {APP_NAME} for infringing or unlawful purposes.
        </p>

        <p>
          This License Agreement and Terms of Use (<strong>"Agreement"</strong>) is
          entered into between <strong>{APP_NAME}</strong> (<strong>"we", "us", "our"</strong>)
          and you, the user (<strong>"you", "Licensee"</strong>), and governs your
          access to and use of the {APP_NAME} web application, services, and related
          materials (collectively, the <strong>"Service"</strong>). By accessing or
          using the Service, you agree to be bound by this Agreement.
        </p>

        <h2>1. Grant of License & Plans</h2>

        <p>
          Subject to this Agreement and any plan-specific limitations set out on our
          pricing or subscription pages, {APP_NAME} grants you a limited,
          non-exclusive, non-transferable, and non-sublicensable license to access
          and use the Service to process media content that you own or are properly
          licensed or authorized to use.
        </p>

        <h3>1.1 Free Plan</h3>
        <p>
          Under the Free Plan, you may access and use the Service with limited
          features, usage quotas, or functionality as indicated on our website. The
          Free Plan is intended for evaluation and low-volume work. All use remains
          subject to this Agreement.
        </p>

        <h3>1.2 Basic Plan</h3>
        <p>
          Under the Basic Plan, you may use the Service with extended limits and
          features compared to the Free Plan, for your own personal or commercial
          projects, subject to the current plan description on our pricing page.
        </p>

        <h3>1.3 Pro Plan</h3>
        <p>
          Under the Pro Plan, you may use the Service with higher limits and
          additional features suitable for professionals, freelancers, and small
          teams working on multiple or client projects. You are responsible for
          ensuring that all individuals using the Service under your Pro subscription
          comply with this Agreement.
        </p>

        <h3>1.4 Developer Plan</h3>
        <p>
          Under the Developer Plan, you may use the Service for advanced and
          higher-volume workflows, including integrations, automation, and, where
          offered, API access or developer features, as described on the pricing or
          documentation pages. You are responsible for any integration of the
          Service into your own applications or systems and for how your end users
          interact with such integrations.
        </p>

        <p>
          Plan features, limits, and pricing may change from time to time. Your
          continued use of the Service after any such changes constitutes your
          acceptance of the updated plan terms.
        </p>

        <h2>2. User Content & Responsibility</h2>
        <p>
          You are solely responsible for all content that you upload, submit, link,
          or process through the Service (<strong>"User Content"</strong>). By using
          the Service, you represent and warrant that:
        </p>
        <ul>
          <li>
            You own or have obtained all necessary rights, licenses, and permissions
            to use and process the User Content through {APP_NAME}.
          </li>
          <li>
            Your use of the Service and User Content does not infringe or violate
            any third-party rights, including copyright, trademark, privacy, or
            publicity rights.
          </li>
          <li>
            Your use of the Service complies with all applicable laws, regulations,
            and the terms of any third-party platforms from which content originates.
          </li>
        </ul>
        <p>
          We do not monitor all User Content and are not responsible for what you
          choose to process. However, we reserve the right to suspend or terminate
          access to the Service if we reasonably believe your use is unlawful or
          violates this Agreement.
        </p>

        <h2>3. Third-Party Platforms & Services</h2>
        <p>
          The Service may be used in conjunction with content obtained from
          third-party platforms or services (e.g., hosting providers, video
          platforms, or storage services). You acknowledge and agree that:
        </p>
        <ul>
          <li>
            {APP_NAME} is not affiliated with, endorsed by, or responsible for any
            third-party platform.
          </li>
          <li>
            It is your responsibility to comply with any applicable terms of service
            or usage rules of such third-party platforms.
          </li>
          <li>
            You must not use the Service to circumvent technical measures,
            restrictions, or access controls implemented by any third-party service.
          </li>
        </ul>

        <h2>4. Restrictions</h2>
        <p>You may not, and may not allow others to:</p>
        <ul>
          <li>
            Use the Service to upload, process, or distribute content for which you
            do not have the necessary rights or permissions.
          </li>
          <li>
            Use the Service to infringe, misappropriate, or violate any copyright,
            trademark, or other intellectual property or proprietary right.
          </li>
          <li>
            Resell, redistribute, or make the Service itself available as a
            standalone product or competing service.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to derive the source code of any
            part of the Service, except as permitted by applicable law.
          </li>
          <li>
            Remove or alter any copyright, trademark, or proprietary notices on the
            Service or related materials.
          </li>
          <li>
            Use the Service in a way that violates any applicable law, regulation,
            or third-party terms, or that is harmful, fraudulent, deceptive, or
            abusive.
          </li>
        </ul>

        <h2>5. Ownership and Intellectual Property</h2>
        <p>
          {APP_NAME} and its licensors retain all right, title, and interest in and
          to the Service, including all software, interfaces, designs, and related
          intellectual property, whether registered or not. This Agreement does not
          transfer any ownership rights to you. You retain ownership of your User
          Content, subject to the rights you grant us to operate and improve the
          Service.
        </p>

        <h2>6. DMCA / Takedown & Complaints</h2>
        <p>
          If you believe that content processed or made available through the
          Service infringes your copyright or other rights, you may contact us at{" "}
          <a href="mailto:support@cutora.io">support@cutora.io</a> with sufficient
          detail to identify the content and your rights. We will review and, where
          appropriate, remove or restrict access to allegedly infringing material in
          accordance with applicable law.
        </p>

        <h2>7. Warranty Disclaimer</h2>
        <p>
          THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT
          WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING
          BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
          THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL
          COMPONENTS.
        </p>

        <h2>8. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL {APP_NAME}, ITS
          OWNERS, DIRECTORS, EMPLOYEES, OR AFFILIATES BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR
          ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR IN
          CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF WE
          HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATED TO
          THIS AGREEMENT OR THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID (IF
          ANY) FOR ACCESS TO THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE
          EVENT GIVING RISE TO THE CLAIM.
        </p>

        <h2>9. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless {APP_NAME} and its
          affiliates, officers, employees, and agents from and against any and all
          claims, liabilities, damages, losses, and expenses (including reasonable
          attorneys&apos; fees) arising out of or in any way connected with: (a)
          your use of the Service; (b) your User Content; or (c) your violation of
          this Agreement or any applicable law or third-party right.
        </p>

        <h2>10. Governing Law and Jurisdiction</h2>
        <p>
          This Agreement shall be governed by and construed in accordance with the
          laws of the Republic of Bulgaria and applicable European Union law,
          without regard to its conflict of law principles. Any dispute arising out
          of or relating to this Agreement or the Service shall be subject to the
          exclusive jurisdiction of the competent courts of Sofia, Bulgaria.
        </p>

        <h2>11. Changes to This Agreement</h2>
        <p>
          We may update or modify this Agreement from time to time. When we do, we
          will update the &quot;Last updated&quot; date below. Your continued use
          of the Service after any such changes constitutes your acceptance of the
          updated Agreement.
        </p>

        <h2>12. Entire Agreement</h2>
        <p>
          This Agreement constitutes the entire agreement between you and {APP_NAME}{" "}
          regarding your use of the Service and supersedes all prior or
          contemporaneous understandings, agreements, or representations, whether
          written or oral, relating to its subject matter.
        </p>

        <p className="license-updated">Last updated: 21 December 2025</p>
        <p className="license-contact">
          Contact: <a href="mailto:support@cutora.io">support@cutora.io</a>
        </p>
      </div>
    </>
  );
};

export default LicenseAgreement;
